import { ChangeDetectionStrategy, Component, inject, input, output, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideTriangleAlert } from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCheckboxImports } from '@spartan-ng/helm/checkbox';
import { HlmDialogImports } from '@spartan-ng/helm/dialog';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { PartnerClient, PartnerService } from '../../../../services/partner.service';

/**
 * The agency creates a shop account for a client. It confirms it may act for the
 * client and accepts the AGB on its behalf; the client gets a mail to take the
 * account over, but the shop can go live before that.
 */
@Component({
  selector: 'app-create-client-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgIcon,
    HlmAlertImports,
    HlmButtonImports,
    HlmCheckboxImports,
    HlmDialogImports,
    HlmFieldImports,
    HlmInputImports,
    HlmSpinnerImports,
  ],
  providers: [provideIcons({ lucideTriangleAlert })],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './create-client-dialog.component.html',
})
export class CreateClientDialogComponent {
  private readonly partnerService = inject(PartnerService);

  readonly open = input(false);
  readonly close = output<void>();
  readonly created = output<PartnerClient>();

  readonly loading = signal(false);
  readonly submitted = signal(false);
  readonly error = signal<string | null>(null);

  readonly form = inject(FormBuilder).nonNullable.group({
    name: ['', [Validators.required, Validators.minLength(2)]],
    email: ['', [Validators.required, Validators.email]],
    acceptOnBehalf: [false, Validators.requiredTrue],
  });

  invalid(field: 'name' | 'email' | 'acceptOnBehalf'): boolean {
    const control = this.form.controls[field];
    return (this.submitted() || control.touched) && control.invalid;
  }

  onDialogState(state: 'open' | 'closed'): void {
    if (state === 'closed' && this.open()) this.dismiss();
  }

  dismiss(): void {
    if (this.loading()) return;
    this.form.reset();
    this.submitted.set(false);
    this.error.set(null);
    this.close.emit();
  }

  submit(): void {
    if (this.loading()) return;
    this.submitted.set(true);
    this.error.set(null);
    if (this.form.invalid) return;

    const { name, email } = this.form.getRawValue();
    this.loading.set(true);
    this.partnerService
      .createClient({ name: name.trim(), email: email.trim(), accept_terms_on_behalf: true })
      .subscribe({
        next: (client) => {
          this.loading.set(false);
          this.form.reset();
          this.submitted.set(false);
          this.created.emit(client);
        },
        error: (err: HttpErrorResponse) => {
          this.loading.set(false);
          this.error.set(
            err.status === 409
              ? err.error?.detail
              : err.status === 429
                ? 'Zu viele neue Konten in kurzer Zeit. Bitte versuche es später erneut.'
                : 'Das Konto konnte nicht angelegt werden. Bitte versuche es erneut.',
          );
        },
      });
  }
}
